import {
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react'
import {
  Box,
  Divider,
  InputBase,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CheckIcon from '@mui/icons-material/Check'
import AddIcon from '@mui/icons-material/Add'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { FONT_WEIGHT, scaledFontSize } from '@/shared/styles/typography'
import { RADIUS } from '@/shared/styles/radii'

// Gemini 流の pill + dropdown プリミティブ。
// trigger = 軸を示す 16px アイコン + 現在値 + ▾、menu = ✓ + 太字ラベル + 淡い説明のリスト。
// 軸名 (録音モード 等) は文字で出さず icon + hover tooltip で示す (= 文字を値だけに絞る引き算)。
// 重要度を色で出さず、選択は ✓ と太字だけで示す (Notion 28 spec)。
export interface PillOption<T> {
  value: T
  label: string
  description?: string
}

// menu の末尾に置く、選択肢でない 1 行 (その場で選択肢を足す口)。押すとその行が 1 行の欄になり、Enter で足して
// menu を閉じる (足した値を選ぶのは呼び出し側)。Escape で行に戻る。足す口以外 (設定画面への移動など) は置かない。
export interface PillAddRow {
  label: string
  placeholder?: string
  onAdd: (text: string) => Promise<void> | void
}

interface Props<T> {
  value: T
  options: PillOption<T>[]
  onChange: (value: T) => void
  // 軸名 (= 何の選択肢か). a11y ラベル兼 hover tooltip の文言に使う (例「録音モード」).
  ariaLabel: string
  // 値の前に置く 16px の軸アイコン. 同形の pill が並ぶとき各 pill の意味を rest で示す.
  // 省略時は値のみ (アイコンなし).
  icon?: ReactNode
  // 'pill' = 丸い capsule + ▾ (録音ヘッダの選択肢群)。'text' = 文字だけ (capsule と ▾ を持たない。Notion の
  // プロパティの値の形。資料の行の種別 = 物 (紙) の後ろの属性で、capsule + ▾ だと隣の toggle 行と同じ形に
  // 見える。hub log/108 決定 42)。どちらも hover で地・押すと同じ menu
  variant?: 'pill' | 'text'
  disabled?: boolean
  // 選択肢の下の足す口 (design-book 02-components/pill-select.md「末尾の 1 行」)
  addRow?: PillAddRow
  // 値が選択肢に無いときに pill に出す字 (足す口と組で使う。例「担当を追加」)
  emptyLabel?: string
  // menu を揃える端。既定は pill = 右 (録音の画面の右上に並ぶ pill 群)・text = 左。ブロックの左の頭に置く pill
  // (管理栄養士の記録の回の選択) は左: 右に揃えると menu が左へはみ出し、値の字の長さで menu の左端が動く
  menuAlign?: 'left' | 'right'
}

export const PillSelect = <T,>({
  value,
  options,
  onChange,
  ariaLabel,
  icon,
  variant = 'pill',
  disabled = false,
  addRow,
  emptyLabel,
  menuAlign = variant === 'text' ? 'left' : 'right',
}: Props<T>) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const open = Boolean(anchor)
  // emptyLabel を渡す呼び出しは「未選択」を持つ (値が選択肢に無ければ先頭に寄せず emptyLabel を出す)
  const active =
    options.find((option) => option.value === value) ??
    (emptyLabel === undefined ? options[0] : undefined)

  const handleOpen = (e: MouseEvent<HTMLButtonElement>) => {
    setAnchor(e.currentTarget)
    // 選択肢が無ければ、開いた時点で足す欄にする (押す手を 1 つ減らす)
    if (addRow && options.length === 0) setAdding(true)
  }
  const handleClose = () => {
    setAnchor(null)
    setAdding(false)
    setDraft('')
  }
  const handleSelect = (next: T) => {
    onChange(next)
    handleClose()
  }
  const handleAddKey = async (e: KeyboardEvent<HTMLInputElement>) => {
    // menu の型の先読み (文字キーで項目へ移る) と Escape で閉じるのを止め、欄の中で持つ
    e.stopPropagation()
    if (e.key === 'Escape') {
      e.preventDefault()
      if (options.length === 0) handleClose()
      else {
        setAdding(false)
        setDraft('')
      }
      return
    }
    if (e.key !== 'Enter' || e.nativeEvent.isComposing || !addRow) return
    e.preventDefault()
    const text = draft.trim()
    if (!text || saving) return
    setSaving(true)
    try {
      await addRow.onAdd(text)
      handleClose()
    } finally {
      setSaving(false)
    }
  }

  const trigger = (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={handleOpen}
      aria-haspopup="listbox"
      aria-label={ariaLabel}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        // 同じ pill 列に並ぶ「録音せずに記載」と揃えて文字サイズ 3 値に追従させる
        // (録音画面は追従スコープ). 器は minHeight にして「大」で文字がはみ出さない形に.
        minHeight: 32,
        px: variant === 'text' ? 0.75 : 1.25,
        border: 'none',
        borderRadius: `${variant === 'text' ? RADIUS.sm : RADIUS.full}px`,
        fontFamily: 'inherit',
        fontSize: scaledFontSize('label'),
        fontWeight: FONT_WEIGHT.medium,
        color: open ? colorTokens.fg.default : colorTokens.fg.muted,
        bgcolor: open ? colorTokens.overlay.hover : 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color 120ms, color 120ms',
        '&:hover': {
          bgcolor: disabled ? 'transparent' : colorTokens.overlay.hover,
          color: colorTokens.fg.default,
        },
        // menu を Esc で閉じると focus が戻り、ブラウザ既定の青い輪が capsule を縁取る。
        // キーボードの印はアプリの形 (hover と同じ地) で出す
        '&:focus-visible': {
          outline: 'none',
          bgcolor: colorTokens.overlay.hover,
          color: colorTokens.fg.default,
        },
      }}
    >
      {icon && (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            mr: 0.5,
            '& svg': { fontSize: 16, display: 'block' },
          }}
        >
          {icon}
        </Box>
      )}
      {active ? active.label : emptyLabel}
      {variant === 'pill' && <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
    </Box>
  )

  return (
    <>
      {disabled ? (
        trigger
      ) : (
        <Tooltip title={ariaLabel} placement="bottom">
          {trigger}
        </Tooltip>
      )}
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={handleClose}
        // 揃える端は menuAlign。文字の形は値の左端に揃えて開く (右端に揃えると、狭い文字の左へ menu がはみ出して
        // 隣の絵に被る)
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: menuAlign,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: menuAlign,
        }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              minWidth: 220,
              borderRadius: `${RADIUS.lg}px`,
              p: 0.5,
              border: `1px solid ${colorTokens.border.subtle}`,
              boxShadow: colorTokens.shadow.popover,
            },
          },
          list: { sx: { py: 0 } },
        }}
      >
        {options.map((option) => {
          const selected = option.value === value
          return (
            <MenuItem
              key={String(option.value)}
              disableRipple
              onClick={() => handleSelect(option.value)}
              sx={{
                alignItems: 'flex-start',
                gap: 1,
                px: 1,
                py: 0.75,
                borderRadius: `${RADIUS.md}px`,
                bgcolor: 'transparent',
                '&:hover': { bgcolor: colorTokens.overlay.hover },
              }}
            >
              <Box sx={{ width: 18, flexShrink: 0, pt: 0.25 }}>
                {selected && (
                  // block にする: inline の svg は字の行の箱を作り、✓ の行だけ 6px 高くなる (説明の行が無い選択で
                  // 選び替えるたびに行がずれて見えた)
                  <CheckIcon
                    sx={{
                      display: 'block',
                      fontSize: 16,
                      color: colorTokens.fg.default,
                    }}
                  />
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: 1.4,
                    color: colorTokens.fg.default,
                  }}
                >
                  {option.label}
                </Typography>
                {option.description && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      lineHeight: 1.4,
                      color: colorTokens.fg.muted,
                    }}
                  >
                    {option.description}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          )
        })}
        {addRow && options.length > 0 && (
          <Divider sx={{ my: 0.5, borderColor: colorTokens.border.subtle }} />
        )}
        {addRow &&
          (adding ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.5,
              }}
            >
              <Box sx={{ width: 18, flexShrink: 0 }} />
              <InputBase
                autoFocus
                value={draft}
                placeholder={addRow.placeholder}
                disabled={saving}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) =>
                  void handleAddKey(e as KeyboardEvent<HTMLInputElement>)
                }
                inputProps={{ 'aria-label': addRow.label }}
                sx={{ flex: 1, fontSize: 14, color: colorTokens.fg.default }}
              />
            </Box>
          ) : (
            <MenuItem
              disableRipple
              onClick={() => setAdding(true)}
              sx={{
                gap: 1,
                px: 1,
                py: 0.75,
                borderRadius: `${RADIUS.md}px`,
                color: colorTokens.fg.muted,
                '&:hover': { bgcolor: colorTokens.overlay.hover },
              }}
            >
              <Box sx={{ width: 18, flexShrink: 0, display: 'flex' }}>
                <AddIcon sx={{ fontSize: 16 }} />
              </Box>
              <Typography sx={{ fontSize: 14, lineHeight: 1.4 }}>
                {addRow.label}
              </Typography>
            </MenuItem>
          ))}
      </Menu>
    </>
  )
}
